import React, { useState, useEffect, useMemo } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const StockHistory = () => {
  const [history, setHistory] = useState([])
  const [filteredHistory, setFilteredHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  })

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await apiClient.get(API_ENDPOINTS.STOCK_TRANSFERS)
        const records = response.data || []
        setHistory(records)
        setFilteredHistory(records)
      } catch (err) {
        console.error('Failed to fetch stock history:', err)
        setError('Unable to load stock history data. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()
  }, [])

  useEffect(() => {
    let filtered = [...history]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(record => {
        const fields = [
          record.transfer_type_display,
          record.status_display,
          record.reference_number,
          record.source_warehouse_name,
          record.source_branch_name,
          record.destination_warehouse_name,
          record.destination_branch_name
        ]
        return fields
          .filter(Boolean)
          .some(field => field.toLowerCase().includes(searchLower))
      })
    }

    if (filters.type) {
      filtered = filtered.filter(record => record.transfer_type === filters.type)
    }

    if (filters.status) {
      const statusFilter = filters.status.toLowerCase()
      filtered = filtered.filter(record => (record.status || '').toLowerCase() === statusFilter)
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(record =>
        new Date(record.created_at) >= new Date(filters.dateFrom)
      )
    }

    if (filters.dateTo) {
      const endDate = new Date(filters.dateTo)
      endDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(record =>
        new Date(record.created_at) <= endDate
      )
    }

    setFilteredHistory(filtered)
  }, [history, filters])

  const movementTypes = useMemo(() => {
    const unique = new Map()
    history.forEach(record => {
      if (!unique.has(record.transfer_type)) {
        unique.set(record.transfer_type, record.transfer_type_display || record.transfer_type)
      }
    })
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }))
  }, [history])

  const statusOptions = useMemo(() => {
    const unique = new Map()
    history.forEach(record => {
      const value = record.status
      const label = record.status_display || value
      if (value && !unique.has(value)) {
        unique.set(value, label || value)
      }
    })
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }))
  }, [history])

  const formatCurrency = (value) =>
    typeof value === 'number'
      ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '$0.00'

  const isCompleted = (record) =>
    record?.status === 'completed' ||
    record?.status_display?.toLowerCase() === 'completed'

  const handleRecordClick = (record) => {
    const event = new CustomEvent('navigateToScreen', {
      detail: {
        screen: 'stock-history-detail',
        params: { movement: record }
      }
    })
    window.dispatchEvent(event)
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Stock History</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products & Inventory</li>
            <li className="breadcrumb-item active">Stock History</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-clock-history me-2"></i>
                  Stock History
                </h5>

                <div className="row mb-3 g-3">
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search history..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={filters.type}
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    >
                      <option value="">All Types</option>
                      {movementTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="">All Statuses</option>
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="From Date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="To Date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                  </div>
                </div>

                {loading ? (
                  <p>Loading stock history...</p>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                ) : filteredHistory.length === 0 ? (
                  <p>No stock history records match your filters.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Transfer Type</th>
                          <th>Total Cost</th>
                          <th>Source</th>
                          <th>Destination</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHistory.map(record => (
                          <tr
                            key={record.id}
                            role="button"
                            onClick={() => handleRecordClick(record)}
                          >
                            <td>{record.transfer_type_display || record.transfer_type}</td>
                            <td>{formatCurrency(Number(record.total_cost) || 0)}</td>
                            <td>{record.source_warehouse_name || record.source_branch_name || 'N/A'}</td>
                            <td>{record.destination_warehouse_name || record.destination_branch_name || 'N/A'}</td>
                            <td>
                              <span className={`badge ${
                                isCompleted(record) ? 'bg-success' :
                                record.status === 'pending' || record.status_display === 'Pending' ? 'bg-warning text-dark' :
                                'bg-secondary'
                              }`}>
                                {record.status_display || record.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default StockHistory

